import { useEffect, useState } from 'react';
import NotificationThreadSection from './NotificationThreadSection';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotificationById,
  getNotificationSenderTitle,
  markNotificationAsSeen,
} from '../services/notificationService';

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function ParentNotificationModal({ notificationId, onClose, onSeen }) {
  const { user } = useAuth();
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!notificationId) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [notificationId]);

  useEffect(() => {
    if (!notificationId) {
      return undefined;
    }

    let isMounted = true;

    async function loadNotification() {
      setIsLoading(true);
      setErrorMessage('');
      setNotification(null);

      try {
        const notificationData = await getNotificationById(notificationId);

        if (!isMounted) {
          return;
        }

        setNotification(notificationData);

        if (notificationData && !notificationData.isSeen) {
          try {
            const seenNotification = await markNotificationAsSeen(notificationId);

            if (isMounted && seenNotification) {
              setNotification(seenNotification);
              onSeen?.(seenNotification);
            }
          } catch {
            if (isMounted) {
              setNotification(notificationData);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت جزئیات اطلاعیه وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadNotification();

    return () => {
      isMounted = false;
    };
  }, [notificationId, onSeen]);

  if (!notificationId) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/45 px-4 pb-6 pt-10 sm:items-center"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85dvh] w-full max-w-app flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_12px_40px_rgba(15,23,42,0.18)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <p className="text-[13px] font-bold text-slate-800">جزئیات اطلاعیه</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[6px] bg-[#F8F8F8] px-3 py-1 text-[11px] font-bold text-slate-600"
          >
            بستن
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="rounded-[14px] bg-[#F8F8F8] p-6 text-center text-[12px] text-slate-500">
              در حال دریافت جزئیات اطلاعیه...
            </div>
          ) : errorMessage ? (
            <div className="rounded-[14px] bg-[#F8F8F8] p-5 text-center">
              <p className="text-[13px] font-bold text-[#E02727]">{errorMessage}</p>
            </div>
          ) : !notification ? (
            <div className="rounded-[14px] bg-[#F8F8F8] p-5 text-center">
              <p className="text-[15px] font-bold text-slate-800">اطلاعیه پیدا نشد</p>
              <p className="mt-2 text-[12px] text-slate-500">این اطلاعیه در دسترس نیست.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className="overflow-hidden rounded-[14px] text-white"
                style={{
                  background: 'linear-gradient(20.43deg, rgba(84, 176, 248, 0.9) 0.9%, rgba(145, 73, 133, 0.9) 86.43%)',
                  boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.14)',
                }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[17px] font-bold leading-7">{notification.title || 'بدون عنوان'}</p>
                      <p className="mt-1 text-[11px] text-white/85">
                        {notification.senderId === user?.id
                          ? 'ارسال شده توسط شما'
                          : `ارسال شده توسط ${getNotificationSenderTitle(notification)}`}
                      </p>
                    </div>

                    <span className="shrink-0 rounded-full bg-white px-3 py-1 text-[10px] font-bold text-[#914985]">
                      {notification.isSeen ? 'خوانده شده' : 'جدید'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 text-[10px]">
                    <div className="rounded-[7px] bg-white/20 px-3 py-2">
                      <p className="font-light text-white/80">نوع پیام</p>
                      <p className="mt-1 font-bold">{notification.typeNotification || 'Notification'}</p>
                    </div>

                    <div className="rounded-[7px] bg-white/20 px-3 py-2">
                      <p className="font-light text-white/80">زمان ارسال</p>
                      <p className="mt-1 font-bold">{formatNotificationDate(notification.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <NotificationThreadSection
                notification={notification}
                currentUserId={user?.id}
                accent="green"
              />

              {notification.senderId !== user?.id ? (
                <p className="text-center text-[11px] font-light text-slate-500">
                  با باز کردن این اطلاعیه، به صورت خودکار خوانده شده ثبت می‌شود.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ParentNotificationModal;
